import { AxiosInstance } from 'axios';
import { ClaimManagementPort, ClaimsPort, ItemClaimsPort, ItemPort } from '../core/api/ports';

/**
 * Contrato tipado de un cliente (tenant). Cada `tenants/<cliente>/config.ts` implementa `TenantConfig`:
 * si falta una clave o un valor no es válido, falla al compilar.
 *
 * Se usan string enums (no `const enum`) para que funcionen con Babel / Vue CLI.
 */

export enum TenantId {
    Zurich = 'zurich',
    Tasaciones = 'tasaciones',
    Surco = 'surco'
}

export enum AuthStrategyType {
    UrlToken = 'url-token',
    Login = 'login'
}

/**
 * Cómo se accede a una ruta. Es fijo por `RouteKey` (`src/app/route-registry.ts`), no por tenant: un mismo tenant
 * puede mezclar rutas de sesión y por token. Lo resuelve `AccessCoordinator` (`core/auth`) en cada navegación.
 */
export enum RouteAccess {
    /** Sin credenciales (login, baja de cuenta, 404). */
    Public = 'public',
    /** Requiere la sesión del tenant (`TenantConfig.session`): sin sesión, al login. */
    Session = 'session',
    /** Token en la URL (`:token`), sin login: si es inválido, la página muestra su error. */
    UrlToken = 'url-token'
}

/** Rutas que un tenant puede habilitar. Cada clave tiene su definición en `src/app/route-registry.ts`. */
export enum RouteKey {
    Login = 'login',
    DeleteAccount = 'deleteAccount',
    /** Detalle de un item (lote) con edición de polígonos. */
    Item = 'item',
    /** Siniestros de un item en acordeón (acceso por token). */
    ItemClaims = 'itemClaims',
    ClaimsList = 'claimsList',
    ClaimDetail = 'claimDetail'
}

export enum LogoSource {
    /** Logo empaquetado en `tenants/<cliente>/assets/`. */
    Static = 'static',
    /** Logo que devuelve el backend en `/me/` (`customer_logo`). */
    FromMe = 'from-me'
}

export enum BlockKey {
    Indicators = 'indicators',
    PolygonTools = 'polygonTools',
    Map = 'map',
    MapLayers = 'mapLayers',
    SurfaceSummary = 'surfaceSummary',
    Inspection = 'inspection',
    Notes = 'notes',
    ClimateChart = 'climateChart',
    SaveButton = 'saveButton'
}

export enum InspectionTab {
    Appraisers = 'appraisers',
    Notes = 'notes',
    SamplingReport = 'samplingReport'
}

/** Claves del modelo común de indicadores (no del JSON crudo de cada backend). */
export enum IndicatorKey {
    CreationDate = 'creation_date',
    ClaimId = 'claim_id',
    EventType = 'event_type',
    EventDate = 'event_date',
    CropType = 'crop_type',
    DeclaredArea = 'declared_area',
    Area = 'area',
    Department = 'department',
    Province = 'province',
    Season = 'campaign'
}

export enum IndicatorFormat {
    /** Valor tal cual (`-` si está vacío). */
    Text = 'text',
    /** Fecha `dd/mm/aaaa` (`formatDate(value, false, true)`). */
    Date = 'date',
    /** Número + ` Has`; con `decimals` se trunca (`parseFloatNumber`). */
    Hectares = 'hectares'
}

/** Aspecto de los indicadores (`LabelWithValue`). */
export enum IndicatorStyle {
    /** La tarjeta del valor ocupa solo su contenido (`fit-content`). */
    Compact = 'compact',
    /** La tarjeta del valor se estira y el texto largo hace wrap. */
    Fluid = 'fluid'
}

/** Cómo se editan los polígonos en `SimpleMap`. */
export enum PolygonEditingMode {
    /**
     * Menú contextual (hover) con "Modificar Polígono", "Modificar Coordenadas" y "Eliminar Polígono".
     * Los MultiPolygon se editan como una sola feature.
     */
    ContextMenu = 'context-menu',
    /**
     * Menú contextual solo con "Modificar Polígono" + modo "Seleccionar polígonos" (eliminar / quedarse con los
     * seleccionados). Los MultiPolygon se separan en polígonos hermanos (`parentId`) y se rearman al guardar.
     */
    Selection = 'selection'
}

/** Herramientas para cargar polígonos en la página de item (`ItemActions`). */
export enum PolygonTool {
    PreloadedItems = 'preloadedItems',
    Draw = 'draw',
    Coordinates = 'coordinates',
    UploadFile = 'uploadFile',
    /** "Buscar por punto (lat/lng)": centra el mapa en un punto (decimal o GMS). También en el modal de lotes. */
    PointSearch = 'pointSearch'
}

/** Qué hace la página de item al guardar o al salir. */
export enum ItemExitMode {
    /** Navega a la `redirect_url` del PATCH (o a la del GET) tras 1 s. */
    RedirectFromSaveResponse = 'redirect-from-save-response',
    /** Intenta cerrar la ventana a los 500 ms y navega a la `redirect_url` del GET a los 1300 ms. */
    CloseWindowThenRedirect = 'close-window-then-redirect'
}

export interface FeatureFlags {
    /** Permite editar / restaurar el polígono desde el detalle del siniestro. */
    editPolygonInClaim: boolean;
    /** Permite dividir el item ("Dividir Item de Siniestro"). */
    splitPolygon: boolean;
    /**
     * Muestreo: clasificaciones de muestras, marcadores de muestra en el mapa, chips "Muestra" en las notas y la
     * pestaña "Acta de tasación" (esta última además requiere el privilegio `CLAIM` del item).
     */
    samplingReport: boolean;
    /** Línea de "Fecha evento" en el gráfico climático/satelital. */
    eventDateLine: boolean;
    /** Todos los mapas arrancan en vista satelital (`hybrid`). */
    satelliteMapByDefault: boolean;
    /** Muestra el autor (`user_name`) en las notas. */
    noteAuthor: boolean;
}

export interface MapConfig {
    polygonEditing: PolygonEditingMode;
    /** Margen (px) al encuadrar polígonos/capas para que no queden debajo de la leyenda y los controles. */
    fitBoundsPadding: boolean;
}

/** Opciones de cantidad de colores de los modales de análisis. */
export interface AnalysisConfig {
    zonificationScales: number[];
    ndviDiffScales: number[];
}

export interface BrandingColors {
    /** Color `primary` del tema de Vuetify. */
    primary: string;
    /** Color `info` del tema de Vuetify. */
    info: string;
    /** Fondo de la barra superior (`.has-smartway-background`), expuesto como `--tenant-brand-background`. */
    brandBackground: string;
    /**
     * Opcional: recuadro detrás del logo del header (p. ej. `#ffffff` cuando el logo de la aseguradora tiene el mismo
     * tono que `brandBackground` y no se distingue). Sin valor, el logo va sin recuadro.
     */
    logoBackground?: string;
}

export interface TenantLogo {
    source: LogoSource;
    /** Solo cuando `source === LogoSource.Static`: asset importado desde `tenants/<cliente>/assets/`. */
    staticAsset?: string;
}

export interface TenantBranding {
    /** Título que se muestra en la barra superior. */
    title: string;
    /** Título de la barra en negrita. */
    titleBold: boolean;
    /** `document.title` de todas las páginas. */
    documentTitle: string;
    logo: TenantLogo;
    colors: BrandingColors;
    indicatorStyle: IndicatorStyle;
    /**
     * Variante de comboboxes (`.multiselect-overall`): etiqueta de 13px y alto mínimo de 43px (bloque final de
     * `styles.scss` bajo `.tenant-floating-combobox-labels`). La etiqueta flotante base es global para todos los tenants.
     */
    floatingComboboxLabels: boolean;
    /**
     * Favicon propio: asset de `tenants/<cliente>/assets/` (`require('./assets/favicon.png')`) o URL absoluta.
     * Lo aplica `applyBrandingFavicon` (`composition-root.ts`) al arrancar. Si no se define, queda `public/favicon.png`.
     */
    favicon?: string;
}

/**
 * Sesión del tenant: obligatoria si alguna de sus rutas es `RouteAccess.Session` (si falta, la app falla al arrancar).
 * Las rutas `RouteAccess.UrlToken` no la necesitan.
 */
export interface SessionConfig {
    type: AuthStrategyType.Login;
    /** Ruta de login (`RouteAccess.Public`, habilitada en `routes`). */
    loginRoute: RouteKey;
    /** Ruta a la que se redirige a un usuario con sesión que entra a `/` o al login (habilitada en `routes`). */
    landingRoute: RouteKey;
}

export interface IndicatorConfig {
    key: IndicatorKey;
    label: string;
    format?: IndicatorFormat;
    /** Solo `IndicatorFormat.Hectares`: decimales a conservar (trunca). Sin definir: valor tal cual. */
    decimals?: number;
}

/** Página de item (`RouteKey.Item`). */
export interface ItemPageConfig {
    indicators: IndicatorConfig[];
    /** Herramientas de carga de polígonos habilitadas (`ItemActions` las agrupa en tarjetas fijas). */
    polygonTools: PolygonTool[];
    /** Los polígonos cargados (archivo, lotes, coordenadas) se suman a los del mapa salvo que se marque "Sobreescribir". */
    mergeLoadedPolygons: boolean;
    /** Muestra el centroide que viene en `centroid_lat`/`centroid_long` de la `redirect_url`. */
    centroidFromRedirectUrl: boolean;
    exitMode: ItemExitMode;
    /** Composición declarativa (documental): la página todavía se arma a mano, ver ADR 0004. */
    blocks: BlockKey[];
}

/** Detalle de un siniestro: `RouteKey.ClaimDetail` y cada panel de `RouteKey.ItemClaims`. */
export interface ClaimDetailPageConfig {
    indicators: IndicatorConfig[];
    /** Composición declarativa (documental): la página todavía se arma a mano, ver ADR 0004. */
    blocks: BlockKey[];
    /** Pestañas de la sección "Inspección" (si la página la usa), en orden. */
    inspectionTabs: InspectionTab[];
}

/** Siniestros de un item (`RouteKey.ItemClaims`): indicadores del item; cada siniestro usa `claimDetail`. */
export interface ItemClaimsPageConfig {
    indicators: IndicatorConfig[];
}

export interface PagesConfig {
    item?: ItemPageConfig;
    itemClaims?: ItemClaimsPageConfig;
    claimDetail?: ClaimDetailPageConfig;
}

/**
 * Conjunto de puertos de API que expone el adaptador de un tenant.
 * `claims` e `item` son comunes; los demás existen solo si el backend los soporta (y si una ruta habilitada los
 * necesita, `composition-root.ts` falla al arrancar cuando faltan).
 */
export interface TenantApi {
    claims: ClaimsPort;
    item: ItemPort;
    /** Lista/detalle de siniestros y edición del polígono del siniestro (backend tipo tasaciones). */
    claimManagement?: ClaimManagementPort;
    /** Item con sus siniestros y clasificaciones de muestreo (backends con acceso por token y muestreo). */
    itemClaims?: ItemClaimsPort;
}

/** Construye el adaptador de API del tenant sobre el cliente HTTP compartido (con los interceptores de auth). */
export type TenantApiFactory = (http: AxiosInstance) => TenantApi;

/** Lo que una aseguradora puede cambiar de la página de item (`RouteKey.Item`, vista de suscripción). */
export interface CustomerItemPageOverrides {
    /** Indicadores de `pages.item.indicators` que no se muestran para esta aseguradora. */
    hiddenIndicators?: IndicatorKey[];
}

/** Lo que una aseguradora puede cambiar de cada página. Hoy solo la de item. */
export interface CustomerPagesOverrides {
    item?: CustomerItemPageOverrides;
}

/**
 * Perfil de una aseguradora (`customer` de `/me/`) dentro del tenant: todo lo que cambia para ella respecto del
 * config del tenant. Cada clave es opcional y pisa solo lo que define; lo que falta sale del tenant.
 */
export interface CustomerProfile {
    /** Colores que pisan `branding.colors` (solo las claves con valor). */
    colors?: Partial<BrandingColors>;
    /** Ajustes por página. */
    pages?: CustomerPagesOverrides;
}

/**
 * Perfiles por aseguradora: clave = id de `customer` del backend (`/me/`: `customer`). Los ids son datos del backend:
 * solo van en `tenants/<cliente>/config.ts`. Se resuelven con las funciones puras de `core/customer`.
 */
export type CustomerProfiles = Record<number, CustomerProfile>;

export interface TenantConfig {
    id: TenantId;
    /** Solo si el tenant tiene rutas `RouteAccess.Session`. */
    session?: SessionConfig;
    branding: TenantBranding;
    api: TenantApiFactory;
    routes: RouteKey[];
    features: FeatureFlags;
    map: MapConfig;
    analysis: AnalysisConfig;
    pages: PagesConfig;
    /**
     * Opcional: perfiles por aseguradora (colores del header/tema, ajustes de páginas). Sin mapa o con un customer
     * desconocido, todo queda como define el tenant. Si existe, el tenant pide `/me/` (`CustomerContext`).
     */
    customers?: CustomerProfiles;
}
